// One switch's live port read, and what it came to for the comparison (#1264).
//
// **A module of its own so that a page importing these does not import the
// topology.** They lived in `Topology.tsx`, and the site's family tables
// (`FamilyTables.tsx`, eager through `Devices`) took them from there, which
// made the whole drawing eager: 42.3 kB of the main chunk on 2026-10-02, for a
// picture only two lazy routes render. The topology, the device page's
// neighbourhood and the family tables still read a switch through this one
// component, so there is still one implementation of the read and of what it
// came to.
import { useEffect, useRef } from "react";
import { api } from "../../api/client";
import { useQuery, type QueryState } from "../../api/hooks";
import type { FanOut } from "./fanOut";
import { documentKeyedRead, reportedNeighbours, type ReportedNeighbour } from "./keyedBy";
import type { LiveSet, Observation } from "./verdicts";

/** What one switch's live read came to, as the section keeps it: the
 * status and the answer or the problem, and nothing that changes identity
 * from one render to the next. */
export interface Read {
  status: QueryState<LiveSet>["status"]; data?: LiveSet; problem?: { message?: string; reason?: string };
  /** The neighbours a report-keyed answer names, by the report's own keys
   * (ADR-0351), beside `data`, which is the answer through `documentKeyed`.
   * Only the site's drawing reads them; absent for a document-keyed answer. */
  reported?: ReportedNeighbour[];
}

/** One switch's live read, keyed by serial so a hook per switch can be
 * rendered as a component and the section stays one list of them. A change
 * of `tick` reads the switch again as a reload, not a new key: the last
 * answer stays until the new one lands, where keying on `tick` dropped it and
 * blanked the drawing for the round trip (#1049). The read waits for a slot of
 * the page's `fanOut` (#1786), so a page asks for every switch and has no
 * more than `state_fan_out` of them in flight; while it waits it is still
 * being read. */
export function SwitchRead({ serial, tick, fanOut, onRead }: { serial: string; tick: number; fanOut: FanOut; onRead(serial: string, r: Read): void }) {
  // A read still waiting for its slot when this switch leaves the page is not
  // started.
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  // Through `documentKeyed` (ADR-0266): the comparison, the drawing and the
  // family tables all name a port by a configuration's word, and a
  // report-keyed answer holds none. The neighbours it names go up beside it,
  // for the drawing alone (ADR-0351).
  const raw = useQuery(() => fanOut.run(() => api.GET("/switches/{serial}/ports/state", { params: { path: { serial } } }), () => live.current), [serial]);
  const q = documentKeyedRead(raw);
  const reported = reportedNeighbours(raw.data);
  const { reload } = q;
  const seen = useRef(tick);
  useEffect(() => { if (seen.current !== tick) { seen.current = tick; reload(); } }, [tick, reload]);
  const problem = q.status === "error" ? q.problem : undefined;
  // Reported upward as its content changes; the section keeps a map of them.
  useEffect(() => { onRead(serial, { status: q.status, data: q.data, problem, ...(reported ? { reported } : {}) }); }, [serial, q.status, q.data, problem, reported, onRead]);
  return null;
}

/** What a read came to, for the comparison. A switch being read again is
 * judged on its last answer until the new one lands (#1049); a read that
 * failed is unread whatever it answered before. */
export function observationOf(r: Read | undefined): Observation {
  if (r?.status === "loading" && r.data) return { kind: "ready", set: r.data };
  if (!r || r.status === "loading" || r.status === "idle") return { kind: "unread", why: "the switch is still being read" };
  if (r.status === "error") return { kind: "unread", why: `the switch could not be read: ${r.problem?.message ?? r.problem?.reason ?? "no answer"}` };
  if (!r.data) return { kind: "unread", why: "the switch is still being read" };
  return { kind: "ready", set: r.data };
}
